import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import isNull from 'lodash/isNull';
import type { ChartDataEntry, ChartDataSeries } from '../common';
import { stackedBarClasses } from '../common';
import { FireflyBaseChartComponent } from '../common/base-components/base-chart-component';

@Component({
  selector: 'f-stacked-bar-chart',
  templateUrl: './stacked-bar-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FireflyStackedBarChartComponent extends FireflyBaseChartComponent implements OnChanges {
  @Input() data!: ChartDataSeries[];
  @Input() gapBetweenSeries = true;
  @Input() limitedBarMaxWidth = true;
  @Input() infoBubbleScaleFactor = 1;
  @Input() barClasses: string[] = stackedBarClasses;
  @Input() infoBubbleClasses: string[] = ['f-bg-bubble'];
  @Input() infoBubbleTextClasses: string[] = ['f-text-bubble', 'fw-bold'];
  @Input() totalAvailableLegendLabel?: string;
  @Input() totalAvailableLegendMarkerClass?: string;

  protected legendRootClass = 'f-stacked-bar-chart-legend';

  @Output() barClick: EventEmitter<{ data: ChartDataSeries } | undefined | null> = new EventEmitter();

  chartComponent = FireflyStackedBarChartComponent;

  ngOnChanges() {
    if (this.data?.some(entry => typeof entry.totalAvailableValue === 'number' && entry.totalAvailableValue > 0)) {
      this.totalAvailableLegendLabel ??= 'volume';
      this.totalAvailableLegendMarkerClass ??= 'f-stacked-bar-total';
    }
    this.applyTotalAvailableToData();
    super.ngOnChanges();
    this.updateLegend();
  }

  onStackedBarClick($event: { data: ChartDataSeries } | null) {
    this.barClick.emit($event);
  }

  protected shouldAdjustYScaleDomain(threshold: number) {
    const optionalData = this.data.filter(d => !!d.optional);
    if (!optionalData.length) return;
    return optionalData.some(item => {
      const sum = (item.series as ChartDataEntry[]).reduce((acc, i) => acc + (isNull(i.value) ? 0 : i.value!), 0);
      return sum > threshold;
    });
  }

  protected override shouldAdjustDataLabelDomain(threshold: number): boolean {
    return (this.data as ChartDataSeries[]).some(d => {
      const sum = (d.series as ChartDataEntry[]).reduce((acc, i) => acc + (isNull(i.value) ? 0 : i.value!), 0);
      return sum > threshold;
    });
  }

  protected applyTotalAvailableToData() {
    this.data = this.data.map(entry => {
      const totalAvailable = entry.totalAvailableValue;
      if (typeof totalAvailable === 'number' && totalAvailable > 0) {
        // Strip any existing total-available segment to make this operation idempotent
        const series = (entry.series as ChartDataEntry[]).filter(s => s.name !== this.totalAvailableLegendLabel);
        const consumed = series.reduce((acc, s) => acc + (s.value || 0), 0);
        const remaining = Math.max(0, totalAvailable - consumed);
        if (remaining > 0) {
          return {
            ...entry,
            series: [
              ...series,
              {
                name: this.totalAvailableLegendLabel!,
                value: remaining,
                totalAvailableValue: totalAvailable,
                cssClass: this.totalAvailableLegendMarkerClass
              }
            ]
          };
        }
        return { ...entry, series };
      }
      return entry;
    });
  }

  protected updateLegend() {
    if (!this.legendOptions || !this.totalAvailableLegendLabel) return;
    const label = this.totalAvailableLegendLabel;
    const markerCssClass = this.totalAvailableLegendMarkerClass ?? 'f-stacked-bar-total';
    const exists = this.legendOptions.data.some(d => d.label === label);
    if (!exists) {
      this.legendOptions = {
        ...this.legendOptions,
        data: [
          ...this.legendOptions.data,
          {
            label,
            markerCssClass
          }
        ]
      };
    }
  }
}
